type RequestFunction<T> = () => Promise<T>;

interface QueuedRequest<T> {
  request: RequestFunction<T>;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
}

class RequestQueue {
  private queue: QueuedRequest<unknown>[] = [];
  private activeCount = 0;
  private readonly maxConcurrent: number;

  constructor(maxConcurrent = 3) {
    this.maxConcurrent = maxConcurrent;
  }

  async add<T>(request: RequestFunction<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.queue.push({
        request: request as RequestFunction<unknown>,
        resolve: resolve as (value: unknown) => void,
        reject,
      });
      this.process();
    });
  }

  private async process() {
    if (this.activeCount >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    const item = this.queue.shift();
    if (!item) return;

    this.activeCount++;

    try {
      const result = await item.request();
      item.resolve(result);
    } catch (error) {
      item.reject(error);
    } finally {
      this.activeCount--;
      this.process(); // Process next item in queue
    }
  }
}

// Create a singleton instance for dashboard requests
export const dashboardRequestQueue = new RequestQueue(3);
